import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const EmpList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch employees from API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Handle both single object and array responses
        if (Array.isArray(data.reverse())) {
          setEmployees(data);
        } else {
          setEmployees([data]);
        }
        
        setError(null);
      } catch (err) {
        setError(err.message);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

    useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem('userMobile');
      if (!userId) {
        navigate('/');
      }
    };

    checkAuth();
  }, [navigate]);

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    
    return employees.filter(employee => 
      Object.values(employee).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [employees, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
      </li>
    );

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    // Next button
    buttons.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </li>
    );

    return buttons;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>Failed to load employee data: {error}</p>
          <hr />
          <button 
            className="btn btn-outline-danger" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-5 py-5">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title mb-0">Employee List</h3>
            </div>
            <div className="card-body">
              {/* Search Bar */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <svg width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                        <path d="m13.498 12.04-4.73-4.73a5.5 5.5 0 1 0-.707.707l4.73 4.73a.5.5 0 0 0 .707-.707zM6.5 12a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by any field..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-md-end mt-2 mt-md-0">
                  <span className="text-muted">
                    Showing {currentEmployees.length} of {filteredEmployees.length} employees
                  </span>
                </div>
              </div>

                {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav aria-label="Employee list pagination">
                    <ul className="pagination">
                      {renderPaginationButtons()}
                    </ul>
                  </nav>
                </div>
              )}

              {/* Table */}
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      {/* <th scope="col">ID</th> */}
                      <th scope="col">Full Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Mobile Number</th>
                      <th scope="col">Employee ID</th>
                      <th scope="col">Role</th>
                      <th scope="col">Approval Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEmployees.length > 0 ? (
                      currentEmployees.map((employee) => (
                        <tr key={employee.id}>
                          {/* <td>{employee.id}</td> */}
                          <td>
                            <div className="d-flex align-items-center">
                              {/* <div className="avatar-circle me-2">
                                {employee.full_name ? employee.full_name.charAt(0).toUpperCase() : 'N'}
                              </div> */}
                              {employee.full_name}
                            </div>
                          </td>
                          <td>{employee.email}</td>
                          <td>{employee.mobile_number}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {employee.employee_id}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              employee.role === 'admin' ? 'bg-danger' :
                              employee.role === 'manager' ? 'bg-warning' :
                              'bg-info'
                            }`}>
                              {employee.role ? employee.role.charAt(0).toUpperCase() + employee.role.slice(1) : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              employee.approval_status === 'true' || employee.approval_status === true 
                                ? 'bg-success' 
                                : 'bg-warning text-dark'
                            }`}>
                              {employee.approval_status === 'true' || employee.approval_status === true 
                                ? 'Approved' 
                                : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="text-muted">
                            <svg width="48" height="48" fill="currentColor" className="bi bi-inbox mb-3" viewBox="0 0 16 16">
                              <path d="M4.98 1a.5.5 0 0 0-.39.188L1.54 5H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 5h4.46l-3.05-3.812A.5.5 0 0 0 11.02 1H4.98zm9.954 5H10.45a2.5 2.5 0 0 1-4.9 0H1.066l.32 2.562A.5.5 0 0 0 1.884 9h12.234a.5.5 0 0 0 .496-.438L14.933 6z"/>
                            </svg>
                            <p>No employees found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .avatar-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #007bff;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
        }
        
        .table th {
          font-weight: 600;
          border-top: none;
        }
        
        .table-hover tbody tr:hover {
          background-color: rgba(0, 123, 255, 0.1);
        }
        
        .card {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
          border: 1px solid rgba(0, 0, 0, 0.125);
        }
        
        .page-link {
          color: #007bff;
          border-color: #dee2e6;
        }
        
        .page-link:hover {
          color: #0056b3;
          background-color: #e9ecef;
          border-color: #dee2e6;
        }
        
        .page-item.active .page-link {
          background-color: #007bff;
          border-color: #007bff;
        }
      `}</style>
    </div>
  );
};

export default EmpList;